import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AnthropicIcon, OpenAIIcon, GeminiIcon, GroqIcon } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  getAiProvider,
  getAiModuleConfigs,
  getAiUsageStats,
  getAiUsageByUser,
  getAiUsageByModule,
  setAiApiKey,
  removeAiApiKey,
  testAiConnection,
  updateAiModuleConfig,
  toggleAiModule,
  type AiModuleConfig,
  type AiModel,
} from '@/lib/ai';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  Check,
  Eye,
  EyeOff,
  Key,
  Loader2,
  Settings2,
  Sparkles,
  BarChart3,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/settings/ai')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'ai' } });
  },
});

// ─── Provider icon map ────────────────────────────────────────────────────────
const providerIcon: Record<
  string,
  { icon: ComponentType<{ className?: string }>; bg: string; text: string }
> = {
  Anthropic: { icon: AnthropicIcon, bg: 'bg-amber-500/10', text: 'text-amber-600' },
  OpenAI: { icon: OpenAIIcon, bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
  Google: { icon: GeminiIcon, bg: 'bg-blue-500/10', text: 'text-blue-500' },
  Meta: { icon: GroqIcon, bg: 'bg-orange-500/10', text: 'text-orange-500' },
  Mistral: { icon: Key, bg: 'bg-violet-500/10', text: 'text-violet-500' },
  Qwen: { icon: Key, bg: 'bg-pink-500/10', text: 'text-pink-500' },
  NVIDIA: { icon: Key, bg: 'bg-green-500/10', text: 'text-green-500' },
};

type Tab = 'provider' | 'modules' | 'usage';

const tabs: { id: Tab; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'provider', label: 'Provider', icon: Key },
  { id: 'modules', label: 'Modules', icon: Settings2 },
  { id: 'usage', label: 'Usage', icon: BarChart3 },
];

export function AiSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('provider');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
          Intelligence Configuration
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage intelligence provider, model assignments per module, and usage analytics
        </p>
      </div>

      <div className="border-b border-border">
        <nav className="flex gap-6" aria-label="AI settings tabs">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors cursor-pointer',
                activeTab === id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <span className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </span>
              {activeTab === id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-hygge rounded-t-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'provider' && <ProviderTab />}
      {activeTab === 'modules' && <ModulesTab />}
      {activeTab === 'usage' && <UsageTab />}
    </div>
  );
}

// ─── Provider Tab ─────────────────────────────────────────────────────────────

function ProviderTab() {
  const queryClient = useQueryClient();
  const { data: provider, isLoading } = useQuery({
    queryKey: ['ai', 'provider'],
    queryFn: getAiProvider,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const saveMutation = useMutation({
    mutationFn: () => setAiApiKey(apiKey),
    onSuccess: () => {
      setIsEditing(false);
      setApiKey('');
      queryClient.invalidateQueries({ queryKey: ['ai', 'provider'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: removeAiApiKey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'provider'] }),
  });

  const testMutation = useMutation({ mutationFn: testAiConnection });

  if (isLoading || !provider) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  const badgeConfig = {
    admin: {
      label: 'Admin key',
      className: 'text-green-600 border-green-600/30 bg-green-600/5',
      dot: true,
    },
    system: {
      label: 'System default',
      className: 'text-hygge border-hygge/30 bg-hygge/5',
      dot: true,
    },
    none: {
      label: 'Not configured',
      className: 'text-muted-foreground border-border bg-muted/50',
      dot: false,
    },
  } as const;
  const badge = badgeConfig[provider.keySource];

  return (
    <div className="space-y-6">
      {/* Hygge AI card */}
      <Card className="transition-all hover:border-hygge/20">
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-hygge to-hygge/70 shadow-sm ring-1 ring-hygge/20">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold">Hygge Intelligence</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Intelligent AI assistant powering your HR workflows
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn('text-[11px] px-2 py-0.5 font-medium', badge.className)}
              >
                {badge.dot && (
                  <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current" />
                )}
                {badge.label}
              </Badge>
              {provider.keySource !== 'none' && <Check className="h-4 w-4 text-green-600" />}
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">API Key</Label>
                <div className="relative">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-or-..."
                    className="pr-10 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={!apiKey || saveMutation.isPending}
                >
                  {saveMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Save Key
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setApiKey('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                {provider.hasAdminKey ? 'Change Key' : 'Add Key'}
              </Button>
              {provider.keySource !== 'none' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending}
                >
                  {testMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : testMutation.isSuccess ? (
                    testMutation.data?.success ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                    )
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  Test Connection
                </Button>
              )}
              {provider.hasAdminKey && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => removeMutation.mutate()}
                  disabled={removeMutation.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove Key
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Modules Tab ──────────────────────────────────────────────────────────────

const ALL_MODULES = [
  { id: 'task-manager', name: 'Task Manager' },
  { id: 'employees', name: 'Employees' },
  { id: 'projects', name: 'Projects' },
  { id: 'absences', name: 'Absences' },
];

function ModulesTab() {
  const queryClient = useQueryClient();
  const { data: provider } = useQuery({ queryKey: ['ai', 'provider'], queryFn: getAiProvider });
  const { data: configs, isLoading } = useQuery({
    queryKey: ['ai', 'modules'],
    queryFn: getAiModuleConfigs,
  });

  const configMap = useMemo(() => {
    const map = new Map<string, AiModuleConfig>();
    configs?.forEach((c) => map.set(c.module, c));
    return map;
  }, [configs]);

  const updateMutation = useMutation({
    mutationFn: ({ module, modelId }: { module: string; modelId: string }) =>
      updateAiModuleConfig(module, { modelId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'modules'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ module, isEnabled }: { module: string; isEnabled: boolean }) =>
      toggleAiModule(module, isEnabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai', 'modules'] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  const models = provider?.models ?? [];

  // Group models by provider for the select
  const modelsByProvider = models.reduce<Record<string, AiModel[]>>((acc, m) => {
    (acc[m.provider] ??= []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-[1fr_280px_60px] gap-3 px-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Module</span>
        <span>Model</span>
        <span className="text-center">Enabled</span>
      </div>

      {ALL_MODULES.map(({ id, name }) => {
        const config = configMap.get(id);
        const selectedModel = config?.modelId ?? '';
        const isEnabled = config?.isEnabled ?? false;

        // Get icon for selected model's provider
        const selectedProviderName = models.find((m) => m.id === selectedModel)?.provider;
        const theme = providerIcon[selectedProviderName ?? ''] ?? {
          icon: Settings2,
          bg: 'bg-hygge/10',
          text: 'text-hygge',
        };
        const Icon = theme.icon;

        return (
          <Card key={id} className="transition-all hover:border-hygge/20">
            <CardContent className="grid grid-cols-[1fr_280px_60px] gap-3 items-center p-4 px-5">
              <div className="flex items-center gap-3">
                <div
                  className={cn('flex h-8 w-8 items-center justify-center rounded-lg', theme.bg)}
                >
                  <Icon className={cn('h-4 w-4', theme.text)} />
                </div>
                <span className="text-sm font-medium">{name}</span>
              </div>

              <Select
                value={selectedModel}
                onValueChange={(modelId) => updateMutation.mutate({ module: id, modelId })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(modelsByProvider).map(([provName, provModels]) => (
                    <SelectGroup key={provName}>
                      <SelectLabel className="text-[10px] uppercase tracking-wider">
                        {provName}
                      </SelectLabel>
                      {provModels.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex justify-center">
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) =>
                    config
                      ? toggleMutation.mutate({ module: id, isEnabled: checked })
                      : updateMutation.mutate({ module: id, modelId: models[0]?.id ?? '' })
                  }
                />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Usage Tab ────────────────────────────────────────────────────────────────

function UsageTab() {
  const [period, setPeriod] = useState('month');

  const { from } = useMemo(() => {
    const now = new Date();
    switch (period) {
      case 'week':
        return { from: new Date(now.getTime() - 7 * 86_400_000).toISOString() };
      case 'month':
        return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() };
      case 'year':
        return { from: new Date(now.getFullYear(), 0, 1).toISOString() };
      default:
        return { from: undefined };
    }
  }, [period]);

  const { data: stats } = useQuery({
    queryKey: ['ai', 'usage', 'stats', from],
    queryFn: () => getAiUsageStats(from),
  });
  const { data: byUser } = useQuery({
    queryKey: ['ai', 'usage', 'by-user', from],
    queryFn: () => getAiUsageByUser(from),
  });
  const { data: byModule } = useQuery({
    queryKey: ['ai', 'usage', 'by-module', from],
    queryFn: () => getAiUsageByModule(from),
  });

  const fmt = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This week</SelectItem>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="year">This year</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        {stats && stats.totalRequests > 0 && (
          <span className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{fmt(stats.totalTokens)} tokens</span>{' '}
            across <span className="font-medium text-foreground">{stats.totalRequests}</span>{' '}
            requests
          </span>
        )}
      </div>

      {byUser && byUser.length > 0 && (
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-2 border-b border-border">
            By User
          </h2>
          <div className="space-y-2">
            {byUser.map((u) => (
              <Card key={u.employeeId} className="transition-all hover:border-hygge/20">
                <CardContent className="flex items-center justify-between p-4 px-5">
                  <div className="flex items-center gap-3">
                    {u.avatarUrl ? (
                      <img
                        src={u.avatarUrl}
                        alt={u.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-hygge/10 flex items-center justify-center text-xs font-medium text-hygge">
                        {u.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </div>
                    )}
                    <span className="text-sm font-medium">{u.name}</span>
                  </div>
                  <div className="flex items-center gap-6 text-xs text-muted-foreground">
                    <span>{fmt(u.totalTokens)} tokens</span>
                    <span>{u.requests} requests</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {byModule && byModule.length > 0 && (
        <div>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3 pb-2 border-b border-border">
            By Module
          </h2>
          <div className="space-y-2">
            {byModule.map((m) => (
              <Card key={`${m.module}-${m.model}`} className="transition-all hover:border-hygge/20">
                <CardContent className="flex items-center justify-between p-4 px-5">
                  <div>
                    <span className="text-sm font-medium capitalize">
                      {m.module.replace('-', ' ')}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">{m.model}</span>
                  </div>
                  <div className="flex items-center gap-6 text-xs text-muted-foreground">
                    <span>
                      {fmt((m._sum.inputTokens ?? 0) + (m._sum.outputTokens ?? 0))} tokens
                    </span>
                    <span>{m._count} requests</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {(!stats || stats.totalRequests === 0) && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <BarChart3 className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No AI usage data yet</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Usage will appear here once users start using AI features
          </p>
        </div>
      )}
    </div>
  );
}
